import { useState, useEffect, useCallback, memo } from "react";
import Checkbox from "../components/Checkbox";
import { ListRow } from "../components/ListRow";
import ReactTooltip from "react-tooltip";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "../components/Dropdown";
import DownloadLayerItem from "./DownloadLayerItem";
import useEsri from "../hooks/useEsri";
import { GIS_PM_ID_SLUG, DOWNLOAD_FILE_TYPES } from "../settings";
import { DownloadIcon } from "../components/Icons";
import { OpacitySlider } from "../esri-components/OpacitySlider";

const LayerItem = memo(({ name, layerRefId, gpInfo, checked, defaultOpacity }) => {
  const [checkboxState, setCheckboxState] = useState(checked);
  const [downloadItems, setDownloadItem] = useState([]);
  const { setLayerVisible, layerOpacity } = useEsri();

  useEffect(()=>{
    setCheckboxState(checked)
  },[checked]);

  const applyLayerOpacity = useCallback(
    (opacity) => {
      const layerId = `${GIS_PM_ID_SLUG}${layerRefId}`;
      layerOpacity(layerId, opacity);
    },
    [layerOpacity, layerRefId]
  );

  useEffect(() => {
    const layerId = `${GIS_PM_ID_SLUG}${layerRefId}`;
    if (checkboxState) {
      setLayerVisible(layerId, true);
      return;
    }
    setLayerVisible(layerId, false);
  }, [checkboxState, layerRefId, setLayerVisible]);

  useEffect(() => {
    if (defaultOpacity) {
      applyLayerOpacity(defaultOpacity);
    }
  }, [defaultOpacity, applyLayerOpacity]);

  const onDownloadLayerSelect = (formatType) => {
    if (!downloadItems.includes(formatType)) {
      setDownloadItem((prevState) => {
        return [...prevState, formatType];
      });
    }
  };

  const onOpacityChange = (value) => applyLayerOpacity(value);

  const onNotificationClear = (removeIndex) => {
    const removeDownloadItems = [...downloadItems];
    removeDownloadItems.splice(removeIndex, 1);
    setDownloadItem(removeDownloadItems);
  };

  return (
    <ListRow
      name={name}
      actionCheckBox={
        <Checkbox
          onChangeCallback={(e) => {
            checkboxState ? setCheckboxState(false) : setCheckboxState(true);
          }}
          checked={checkboxState}
        />
      }
      actionsNode={
        <>
          <div className="relative mr-2">
            <OpacitySlider
              onFinalChange={onOpacityChange}
              defaultOpacity={defaultOpacity}
              tooltipFor={layerRefId}
            />
            <ReactTooltip id={layerRefId} place="left" effect="solid">
              <span>{name} Layer Transparency</span>
            </ReactTooltip>
          </div>
          <Dropdown testId="measurement_download-test">
            <DropdownToggle>
              <span className="ml-1 hidden md:inline-block">Download</span>
              <span className="mr-1 ml-1">
                <DownloadIcon />
              </span>
              <svg
                className="fill-current h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </DropdownToggle>
            <DropdownMenu>
              {DOWNLOAD_FILE_TYPES.map((menuItem) => (
                <DropdownItem
                  key={menuItem.value}
                  onSelect={() => {
                    onDownloadLayerSelect(menuItem.value);
                  }}
                >
                  {menuItem.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </>
      }
    >
      {downloadItems.length !== 0 && (
        <div className="flex flex-wrap mb-2 text-sm">
          {downloadItems.map((fileType, key) => (
            <DownloadLayerItem
              key={fileType}
              fileType={fileType}
              gpServiceInfo={gpInfo}
              clearNotification={() => {
                onNotificationClear(key);
              }}
            />
          ))}
        </div>
      )}
    </ListRow>
  );
});

export default LayerItem;
